import { Component, Input, OnInit } from '@angular/core';
import { BaseWidgetComponent } from '@components/base/base-widget/base-widget.component';
import { SearchEntitiesService } from '@components/search-results/services/search-entities.service';
import { EventHttpService } from '@core/http/event-http.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { EntityName } from '@shared/enums/entity-name.enum';
import { EventInterface } from '@shared/interfaces/event.interface';
import { ApiGetResponse } from '@shared/interfaces/responses/ApiResponse.interface';
import { PageSizeConfig } from '@shared/models/pagination.model';
import { UIPosition } from '@shared/models/ui.model';
import { JsonParse } from '@src/app/shared/decorators/parse-json';
import { ToBoolean } from '@src/app/shared/decorators/to-boolean';
import { EventUtils } from '@src/app/shared/utils/event-utils';
import { Observable } from 'rxjs';
import { filter, map, share, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-event-tiles',
  templateUrl: './event-tiles.component.html',
  providers: [SearchEntitiesService],
})
export class EventTilesComponent extends BaseWidgetComponent implements OnInit {
  @Input() @JsonParse() eventTags: any;
  @Input() @ToBoolean() showPartnerOrgsEvents;
  data$: Observable<ApiGetResponse<EventInterface>>;

  readonly entityPath = environment.jipUrl.event;

  readonly entityCreationPath = `${this.entityPath}/create`;

  readonly eventLocationFlagPos = new UIPosition('1.5rem', 'unset', '1.5rem');

  isLoading = false;
  isIframe = false;

  skeletonCount = [];

  uiPosition = new UIPosition('unset', '0.75rem', 'unset', '0.5rem');

  constructor(
    public readonly searchEntitiesService: SearchEntitiesService,
    public readonly eventService: EventHttpService,
    protected readonly sessionService: SessionService
  ) {
    super(sessionService);

    this.data$ = this.searchEntitiesService.entities$.pipe(
      filter((arr: any) => arr.length),
      switchMap((res) => res[0].content),
      map((res: any) => {
        const mappedRes = {
          ...res,
          items: this.mapEventState(res.items),
        };

        return mappedRes;
      }),
      share()
    ) as Observable<ApiGetResponse<EventInterface>>;

    this.searchEntitiesService.isSearchLoading$.subscribe((loading) => {
      this.isLoading = loading;
    });
  }

  ngOnInit(): void {
    this.configSearchService();
    this.sessionService.isOnListPage = true;
    this.skeletonCount = [...Array(Number(this.columns))];
  }

  private configSearchService(): void {
    const widgetPageSize =
      Number(this.page_size) || PageSizeConfig.ThreeItemsFirstPage;

    // Decode URL manually
    const decodedUrl = decodeURIComponent(window.location.href);
    const searchParams = new URL(decodedUrl).searchParams;

    const pageSize = Number(searchParams.get('pageSize')) || widgetPageSize;
    this.searchEntitiesService.entities = [{ key: EntityName.Event }];
    this.searchEntitiesService.currentFilter = EntityName.Event;
    this.searchEntitiesService.pageSizeMedium = pageSize;

    // Payload
    const { filterDTO } = this.searchEntitiesService;
    this.org_id =
      searchParams.get('organizationNumber') ||
      searchParams.get('organizationIds') ||
      this.org_id;
    const keepExternalFilter = !this.org_id && this.isHeaderVisible;

    filterDTO.pageSize = pageSize || 3;
    filterDTO.pageIndex = Number(searchParams.get('pageIndex')) || 1;
    filterDTO.showPartnerOrgsEvents = this.showPartnerOrgsEvents;

    if (searchParams.get('state') || searchParams.get('eventState')) {
      filterDTO.EventState =
        searchParams.get('state') || searchParams.get('eventState');
    }
    filterDTO.organizationIds = this.org_id ? [Number(this.org_id)] : [];
    filterDTO.tags = this.eventTags?.map((tag) => tag.event_title) ?? [];

    // check url to determine if the component is in an iframe
    try {
      this.isIframe =
        window.self !== window.top &&
        window.location.href !== window.top.location.href &&
        searchParams.size > 0;
    } catch (e) {
      this.isIframe = true;
    }

    // Mapping Params
    this.show_paginator = searchParams.get('pagination') === 'true';
    this.columns = searchParams.get('columns') || this.columns;
    this.show_creation_btn =
      searchParams.get('showCreationBtn') === 'true' || this.show_creation_btn;
    this.is_open_in_new_tab =
      searchParams.get('openNewTab') === 'true' ||
      this.isIframe ||
      this.is_open_in_new_tab;

    filterDTO['showCreationBtn'] = this.show_creation_btn;

    this.searchEntitiesService.generateSearchResults(keepExternalFilter).then();
  }

  afterJoinEvent(event: EventInterface): void {
    event.hasJoined = true;
  }

  mapEventState(events: EventInterface[]) {
    return events.map((event) => {
      return {
        ...event,
        isFullyBooked: EventUtils.isEventFullyBooked(event),
        isPassedEvent: EventUtils.isPassedEvent(event),
        isCancelled: EventUtils.isCancelledEvent(event),
      };
    });
  }
}
